// ---------------------------------------------------------------------------
// Supplemental Documents List — Request/review supplemental docs for KYB
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';
import { StatusBadge } from './StatusBadge';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SupplementalDocItem {
  id: string;
  caseId: string;
  documentType: string;
  reason: string;
  status: string;
  requestedAt: string;
  uploadedAt: string | null;
  fileName: string | null;
  mimeType: string | null;
}

interface SupplementalDocsListProps {
  /** List of supplemental document requests. */
  docs: SupplementalDocItem[];
  /** Callback to request a new supplemental document. */
  onRequestNew?: (documentType: string, reason: string) => void;
  /** Callback to review (approve/reject) an uploaded document. */
  onReview?: (docId: string, action: 'reviewed' | 'rejected') => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  certificate_of_incorporation: 'Certificate of Incorporation',
  articles_of_association: 'Articles of Association',
  shareholder_register: 'Shareholder Register',
  proof_of_address: 'Proof of Address',
  board_resolution: 'Board Resolution',
  financial_statements: 'Financial Statements',
  tax_certificate: 'Tax Certificate',
  other: 'Other',
};

const DOCUMENT_TYPES = Object.keys(DOCUMENT_TYPE_LABELS);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SupplementalDocsList({
  docs,
  onRequestNew,
  onReview,
}: SupplementalDocsListProps) {
  const f = useFormatters();
  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState<string>(DOCUMENT_TYPES[0]);
  const [reason, setReason] = useState('');

  const handleSubmitRequest = useCallback(() => {
    if (!onRequestNew || !selectedType || reason.trim().length === 0) return;
    onRequestNew(selectedType, reason.trim());
    setShowForm(false);
    setReason('');
  }, [onRequestNew, selectedType, reason]);

  return (
    <div className="space-y-4">
      {/* Document list */}
      <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
        <div className="px-4 py-3 bg-surface-sunken border-b border-hairline flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">
            Supplemental Documents ({docs.length})
          </h3>
          {onRequestNew && (
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="px-3 py-1 text-xs font-medium bg-info-tint text-info rounded hover:bg-info-tint transition-colors"
            >
              {showForm ? 'Cancel' : 'Request Document'}
            </button>
          )}
        </div>

        {docs.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-ink-muted">
            No supplemental documents requested.
          </div>
        ) : (
          <ul className="divide-y divide-hairline" role="list" aria-label="Supplemental documents">
            {docs.map((doc) => (
              <li key={doc.id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink">
                      {DOCUMENT_TYPE_LABELS[doc.documentType] ?? doc.documentType}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-muted truncate">
                      {doc.reason}
                    </p>
                    <div className="mt-1 flex items-center gap-3 text-xs text-ink-muted">
                      <span>
                        Requested: {f.date(doc.requestedAt, { dateStyle: 'medium' })}
                      </span>
                      {doc.uploadedAt && (
                        <span>
                          Uploaded: {f.date(doc.uploadedAt, { dateStyle: 'medium' })}
                        </span>
                      )}
                      {doc.fileName && (
                        <span className="font-mono text-ink-muted">
                          {doc.fileName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ms-4">
                    <StatusBadge value={doc.status} />
                    {onReview && doc.status === 'uploaded' && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => onReview(doc.id, 'reviewed')}
                          className="px-2 py-1 text-xs font-medium bg-primary-tint text-primary rounded hover:bg-primary-tint transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => onReview(doc.id, 'rejected')}
                          className="px-2 py-1 text-xs font-medium bg-danger-tint text-danger rounded hover:bg-danger-tint transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* New request form */}
      {showForm && onRequestNew && (
        <div className="bg-surface-raised rounded-sm shadow p-4 border border-info/30">
          <h4 className="text-sm font-semibold text-ink mb-3">
            Request Supplemental Document
          </h4>
          <div className="space-y-3">
            <div>
              <label
                htmlFor="doc-type-select"
                className="block text-xs font-medium text-ink-muted mb-1"
              >
                Document Type
              </label>
              <select
                id="doc-type-select"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="block w-full rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
              >
                {DOCUMENT_TYPES.map((dt) => (
                  <option key={dt} value={dt}>
                    {DOCUMENT_TYPE_LABELS[dt]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="doc-reason"
                className="block text-xs font-medium text-ink-muted mb-1"
              >
                Reason
              </label>
              <textarea
                id="doc-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="block w-full rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="Explain why this document is needed..."
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSubmitRequest}
                disabled={reason.trim().length === 0}
                className="px-4 py-1.5 text-sm font-medium bg-info text-white rounded hover:bg-info transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
